/**
 * ProfilePage Component (T170 - US11)
 * Main profile management page for all user types
 * UI Design: Extracted from prototypes/figma-bquest-app/src/components/ProviderPortal.tsx settings tab
 */

import { useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Alert } from '@/shared/components/ui/alert';
import { Spinner } from '@/shared/components/Loading/Spinner';
import { EditProfileForm, type EditProfileFormData } from './EditProfileForm';
import { NotificationPreferences } from './NotificationPreferences';
import type { NotificationPreferencesData } from './NotificationPreferences';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
  useUpdateProfile,
  useUploadProfilePhoto,
  useUserProfile,
} from '../hooks/useProfile';

export const ProfilePage = () => {
  const { data: profile, isLoading, isError, error } = useUserProfile();
  const { data: notificationPreferences } = useNotificationPreferences();
  const updateProfileMutation = useUpdateProfile();
  const uploadPhotoMutation = useUploadProfilePhoto();
  const updateNotificationPreferencesMutation = useUpdateNotificationPreferences();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const missingFields = profile?.missingProfileFields ?? [];
  const missingSelf = missingFields.filter((field) => field.selfEditable);
  const missingViaRequest = missingFields.filter((field) => !field.selfEditable);

  // Handle profile update
  const handleProfileSubmit = async (data: EditProfileFormData) => {
    try {
      await updateProfileMutation.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        profilePhoto: data.profilePhoto,
      });

      setSuccessMessage('Profile updated successfully!');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      // Error is handled by the mutation
      console.error('Profile update error:', err);
    }
  };

  // Handle photo upload
  // Backend handles S3 upload and profile update in one operation
  const handlePhotoUpload = async (file: File) => {
    try {
      await uploadPhotoMutation.mutateAsync(file);

      setSuccessMessage('Profile photo updated successfully!');

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (err) {
      console.error('Photo upload error:', err);
    }
  };

  // Handle notification preferences change
  const handleNotificationPreferencesChange = async (preferences: NotificationPreferencesData) => {
    try {
      await updateNotificationPreferencesMutation.mutateAsync({
        emailMessages: preferences.emailMessages,
      });
    } catch (err) {
      console.error('Notification preferences update error:', err);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-6 max-w-md">
          <Alert variant="destructive">
            <h3 className="font-semibold mb-2">Error Loading Profile</h3>
            <p className="text-sm">
              {error instanceof Error
                ? error.message
                : 'Failed to load your profile. Please try again.'}
            </p>
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl text-gray-900">My Profile</h1>
              {profile.profileCompleted ? (
                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                  Profile Complete
                </Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
                  Profile Incomplete
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Success Message */}
          {successMessage && (
            <Alert className="bg-green-50 border-green-200">
              <p className="text-green-800">{successMessage}</p>
            </Alert>
          )}

          {/* Update Error Message */}
          {updateProfileMutation.isError && (
            <Alert variant="destructive">
              <p>Failed to update profile. Please try again.</p>
            </Alert>
          )}

          {/* Photo Upload Error Message */}
          {uploadPhotoMutation.isError && (
            <Alert variant="destructive">
              <p>Failed to upload photo. Please try again.</p>
            </Alert>
          )}

          {updateNotificationPreferencesMutation.isError && (
            <Alert variant="destructive">
              <p>Failed to update notification preferences. Please try again.</p>
            </Alert>
          )}

          {/* Profile Information Card */}
          <Card className="p-6">
            <h2 className="text-xl mb-6 text-gray-900">Account Settings</h2>

            <EditProfileForm
              profile={profile}
              isLoading={updateProfileMutation.isPending}
              onSubmit={handleProfileSubmit}
              onPhotoUpload={handlePhotoUpload}
              isUploadingPhoto={uploadPhotoMutation.isPending}
            />
          </Card>

          {/* Notification Preferences Card */}
          <Card className="p-6">
            <NotificationPreferences
              userRole={profile.role}
              initialPreferences={{
                emailMessages: notificationPreferences?.emailMessages ?? true,
              }}
              onChange={handleNotificationPreferencesChange}
            />
          </Card>

          {/* Profile Completion Status */}
          {!profile.profileCompleted && (
            <Card className="p-6 bg-yellow-50 border-yellow-200">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Complete Your Profile
                  </h3>
                  {missingSelf.length === 0 && missingViaRequest.length === 0 ? (
                    <p className="text-sm text-gray-700">
                      Fill in all required information to complete your profile. A complete profile
                      helps you get the most out of bQuest.
                    </p>
                  ) : (
                    <div className="space-y-3 text-sm text-gray-700">
                      {missingSelf.length > 0 && (
                        <div>
                          <p>Still needed:</p>
                          <ul className="mt-1 list-disc pl-5">
                            {missingSelf.map((field) => (
                              <li key={field.key}>{field.label}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {missingViaRequest.length > 0 && (
                        <p>
                          Updated by the bQuest team:{' '}
                          {missingViaRequest.map((field) => field.label).join(', ')}. Send a profile
                          update request from the Profile tab on your dashboard.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
